import { resolveLanguageCode } from 'src/app/services/helpers/language.util';
import { LocalizedName, LocalizedNameValue } from '../interfaces/localized-name.interface';

/** Extracts the display string from a polymorphic per-language value. */
export function localizedEntryValue(entry: LocalizedNameValue | null | undefined): string | undefined {
  if (entry == null) return undefined;
  return typeof entry === 'string' ? entry : entry.value;
}

/** Base language subtag, lowercased (e.g. `es-ES` -> `es`, `ZH_CN` -> `zh`). */
export function normalizeLangCode(code: string | null | undefined): string {
  return (code ?? '').trim().toLowerCase().split(/[-_]/)[0];
}

/**
 * Map KEY that serves `code`, with regional resolution (HRS-2934): a pt-BR
 * tenant reads legacy maps keyed `pt` (and vice versa) instead of silently
 * falling through to Spanish. Sibling variants never cross.
 */
function regionalKey(name: LocalizedName, code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  const key = resolveLanguageCode(code, Object.keys(name));
  return key !== null && localizedEntryValue(name[key]) !== undefined ? key : undefined;
}

/**
 * The map KEY that `resolveLocalizedName` would display for the active language,
 * mirroring its fallback chain (active -> `es` -> client main language -> first
 * key). Lets a consumer know which language the resolved display value belongs
 * to, so that language is never also shown elsewhere (e.g. as a manual row).
 */
export function resolveLangKey(
  name: LocalizedName | string | null | undefined,
  selectedLang: string,
  mainLang?: string | null,
): string | undefined {
  if (name == null || typeof name === 'string') return undefined;
  return (
    regionalKey(name, selectedLang) ??
    regionalKey(name, 'es') ??
    regionalKey(name, mainLang) ??
    Object.keys(name)[0]
  );
}

/**
 * Resolves a `{ lang: value }` translations map to a single display string for
 * the active app language, with a sensible fallback chain. Each per-language
 * value may be a plain `string` (legacy) or a `{ value, custom }` object.
 * Tolerates a legacy plain-`string` whole map for backward compatibility.
 *
 * Fallback order: active language -> Spanish (`es`) -> client main language ->
 * first available value -> empty string.
 */
export function resolveLangRecord(
  name: LocalizedName | string | null | undefined,
  selectedLang: string,
  mainLang?: string | null,
): string {
  if (name == null) return '';
  if (typeof name === 'string') return name;

  const key =
    regionalKey(name, selectedLang) ??
    regionalKey(name, 'es') ??
    regionalKey(name, mainLang);

  return (
    (key !== undefined ? localizedEntryValue(name[key]) : undefined) ??
    localizedEntryValue(Object.values(name)[0]) ??
    ''
  );
}
